/**
 * @fileoverview Centralized query keys for React Query
 *
 * Uses factory pattern for type-safe, consistent query keys.
 * All keys should be defined here to avoid duplication and typos.
 *
 * @example
 * // In a hook:
 * import { queryKeys } from 'lib/query';
 *
 * const { data } = useQuery({
 *   queryKey: queryKeys.users.list(),
 *   queryFn: () => apiOrchestrator.get('/users'),
 * });
 */

export const queryKeys = {
  // Users
  users: {
    all: ['users'] as const,
    list: () => [...queryKeys.users.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
    profile: () => [...queryKeys.users.all, 'profile'] as const,
    connections: (userId: string) => [...queryKeys.users.all, 'connections', userId] as const,
    stats: (userId: string) => [...queryKeys.users.all, 'stats', userId] as const,
  },

  // Forms
  forms: {
    all: ['forms'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.forms.all, 'all', params] as const,
    byGroup: (groupId: string, dashboard?: boolean) =>
      [...queryKeys.forms.all, groupId, dashboard] as const,
    detail: (id: string) => ['form', id] as const,
    groups: (filters?: Record<string, unknown>) => ['formGroups', filters] as const,
    groupDetail: (id: string) => ['formGroup', id] as const,
    fields: (formId: string) => ['formFields', formId] as const,
    fieldOptions: () => ['field-options'] as const,
    submissions: (formId: string) => [...queryKeys.forms.all, 'submissions', formId] as const,
  },

  // Menu Connections (used by forms feature)
  menuConnections: {
    all: ['menuConnections'] as const,
    list: () => [...queryKeys.menuConnections.all] as const,
  },

  // Fields (standalone for invalidation)
  fields: {
    all: ['fields'] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.notifications.all, 'list', params] as const,
    unread: () => [...queryKeys.notifications.all, 'unread'] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
  },

  // Meetings
  meetings: {
    all: ['meetings'] as const,
    list: () => [...queryKeys.meetings.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.meetings.all, 'detail', id] as const,
    upcoming: () => [...queryKeys.meetings.all, 'upcoming'] as const,
    scheduled: () => [...queryKeys.meetings.all, 'scheduled'] as const,
    accessHub: (userId: string) => [...queryKeys.meetings.all, 'access-hub', userId] as const,
  },

  // Advisor
  advisor: {
    all: ['advisor'] as const,
    recommendations: () => [...queryKeys.advisor.all, 'recommendations'] as const,
    leads: () => [...queryKeys.advisor.all, 'leads'] as const,
    leadDetail: (id: string) => [...queryKeys.advisor.all, 'leads', id] as const,
  },

  // Strapi/CMS
  cms: {
    all: ['cms'] as const,
    courses: () => [...queryKeys.cms.all, 'courses'] as const,
    courseDetail: (id: string) => [...queryKeys.cms.all, 'courses', id] as const,
    articles: () => [...queryKeys.cms.all, 'articles'] as const,
    config: () => [...queryKeys.cms.all, 'config'] as const,
  },

  // Dashboard/Cockpit
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    agenda: () => [...queryKeys.dashboard.all, 'agenda'] as const,
    nextEvents: () => [...queryKeys.dashboard.all, 'nextEvents'] as const,
    calendarEvents: () => [...queryKeys.dashboard.all, 'calendarEvents'] as const,
  },

  // Images
  images: {
    all: ['images'] as const,
    user: (userId: string) => [...queryKeys.images.all, 'user', userId] as const,
    logo: (orgId: string) => [...queryKeys.images.all, 'logo', orgId] as const,
  },

  // Theme
  theme: {
    all: ['theme'] as const,
    logo: () => [...queryKeys.theme.all, 'logo'] as const,
    config: () => [...queryKeys.theme.all, 'config'] as const,
  },
} as const;

// Type helper for query key inference
export type QueryKeys = typeof queryKeys;
